// The users pages' rules (slice 4.5): Beállítások › Felhasználók, Profilom and
// the set-password page. Apart from the pages so they run on Node in the admin
// tests (tests/admin/test_users_model.py).
//
// The API is the authority: it judges every password, refuses the last active
// admin's switch-off or demotion, and refuses a marketer outright. What is here
// is the quick half — the sentence under a field while it is typed, which row
// actions to offer (and which to grey out, and why), the Hungarian line for
// each entry of a user's log, and what the link dialog says about the link's
// mail.

import type { LinkMail, LinkPurpose, MailOutcome, Role, UserEvent, UserRow, UserState } from "@/lib/api";

/** The API's policy (emc_db.users.service.password_problem), word for word. */
export const PASSWORD_MIN = 12;
export const PASSWORD_MAX = 256;

export const ROLE_LABEL: Record<Role, string> = { admin: "Admin", marketer: "Marketing" };

export const ROLES: Role[] = ["admin", "marketer"];

/** Characters as Python counts them: code points, not UTF-16 halves. */
function characters(text: string): number {
  return [...text].length;
}

/** Why the API would refuse this password, or null. */
export function passwordProblem(password: string, email: string): string | null {
  // Blanks do not make it long: the ends do not count, a run counts as one.
  const meaningful = password.trim().split(/\s+/).join(" ");
  if (characters(meaningful) < PASSWORD_MIN) {
    return `A jelszó legyen legalább ${PASSWORD_MIN} karakter.`;
  }
  if (characters(password) > PASSWORD_MAX) {
    return `A jelszó legfeljebb ${PASSWORD_MAX} karakter lehet.`;
  }
  const folded = password.trim().toLowerCase();
  const address = email.trim().toLowerCase();
  const local = address.split("@")[0] ?? "";
  if (folded === address || folded === local) return "A jelszó nem lehet az email-cím.";
  return null;
}

/** The second field of a new password. */
export function repeatProblem(password: string, again: string): string | null {
  if (again === "") return null;
  return password === again ? null : "A két jelszó nem egyezik.";
}

// --- the link ------------------------------------------------------------------

/** The page a link opens. The token rides in the fragment: a browser never
 * sends that part to a server, so no access log and no Referer header ever
 * holds it. */
export const SET_PASSWORD_PATH = "/jelszo";

export function passwordLinkUrl(origin: string, token: string): string {
  return `${origin.replace(/\/+$/, "")}${SET_PASSWORD_PATH}#${encodeURIComponent(token)}`;
}

/** The token out of `location.hash`, or "" when there is none. */
export function tokenFromHash(hash: string): string {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  try {
    return decodeURIComponent(raw).trim();
  } catch {
    return raw.trim();
  }
}

export const LINK_WORD: Record<LinkPurpose, string> = {
  invite: "meghívó link",
  reset: "jelszó-visszaállító link",
};

// --- the link's mail (system mail) ----------------------------------------------

export interface MailNote {
  /** The Callout's tone: done, or "look at this". */
  tone: "success" | "warn";
  text: string;
}

/** What the link dialog says about the mail, under the link. Null: no mail
 * was tried (an answer from before the mail existed). */
export function mailNote(mail: LinkMail | null, email: string): MailNote | null {
  if (mail === null) return null;
  const why = mail.detail ? ` (${mail.detail})` : "";
  switch (mail.outcome) {
    case "sent":
      return { tone: "success", text: `E-mailben elküldtük ide: ${email}. A linket innen is továbbadhatod.` };
    case "logged":
      return {
        tone: "warn",
        text: "A rendszerlevél naplózó módban van (EMC_SYSTEM_MAIL_TRANSPORT=log): e-mail nem ment ki. Küldd el te a linket.",
      };
    case "failed":
      return { tone: "warn", text: `Az e-mail nem ment ki${why}. Küldd el te a linket.` };
    case "unknown":
      return {
        tone: "warn",
        text: `Nem tudni, kiment-e az e-mail${why}. Ha nem érkezik meg, küldd el te a linket.`,
      };
  }
}

const MAIL_RESULT: Record<MailOutcome, string> = {
  sent: "elküldve",
  logged: "csak naplózva, nem ment ki",
  failed: "sikertelen",
  unknown: "nem tudni, kiment-e",
};

function mailLine(detail: Record<string, unknown>): string {
  const what = detail.purpose === "reset" ? "Jelszó-visszaállító e-mail" : "Meghívó e-mail";
  const outcome = typeof detail.outcome === "string" ? detail.outcome : "";
  const result = outcome in MAIL_RESULT ? MAIL_RESULT[outcome as MailOutcome] : outcome || "—";
  const why = outcome === "failed" && typeof detail.error === "string" && detail.error ? `: ${detail.error}` : "";
  return `${what} ${result}${why}`;
}

// --- what an admin may do with a row ---------------------------------------------

export type UserActionKind = "promote" | "demote" | "deactivate" | "reactivate" | "link";

export interface UserAction {
  kind: UserActionKind;
  label: string;
  /** Why it is greyed out; null when it can be done. */
  blocked: string | null;
  danger?: boolean;
}

export const LAST_ADMIN_BLOCK = "Ő az utolsó aktív admin.";

export function activeAdmins(rows: UserRow[]): number {
  return rows.filter((row) => row.role === "admin" && row.state === "active").length;
}

/** The actions a row offers, in the order the menu lists them. */
export function userActions(row: UserRow, rows: UserRow[]): UserAction[] {
  const lastAdmin = row.role === "admin" && row.state === "active" && activeAdmins(rows) <= 1;
  const actions: UserAction[] = [];
  if (row.role === "marketer") {
    actions.push({ kind: "promote", label: "Admin jog adása", blocked: null });
  } else {
    actions.push({
      kind: "demote",
      label: "Marketing szerepre állítás",
      blocked: lastAdmin ? LAST_ADMIN_BLOCK : null,
    });
  }
  if (row.state === "active") {
    actions.push({ kind: "link", label: "Jelszó-visszaállító link", blocked: null });
  } else if (row.state === "invited" || row.state === "expired") {
    actions.push({ kind: "link", label: "Új meghívó link", blocked: null });
  }
  if (row.state === "deactivated") {
    actions.push({ kind: "reactivate", label: "Visszakapcsolás", blocked: null });
  } else {
    actions.push({
      kind: "deactivate",
      label: row.state === "active" ? "Letiltás" : "Meghívás visszavonása",
      blocked: lastAdmin ? LAST_ADMIN_BLOCK : null,
      danger: true,
    });
  }
  return actions;
}

// --- the log ------------------------------------------------------------------------

const STATE_WORD: Record<UserState, string> = {
  invited: "meghívva",
  active: "aktív",
  expired: "lejárt meghívó",
  deactivated: "letiltva",
};

function roleWord(value: unknown): string {
  return typeof value === "string" && value in ROLE_LABEL ? ROLE_LABEL[value as Role] : String(value ?? "—");
}

function stateWord(value: string | null): string {
  return value !== null && value in STATE_WORD ? STATE_WORD[value as UserState] : String(value ?? "—");
}

/** Who did it, in the screen's words: a person's address, or what ran. */
export function actorLabel(actor: string): string {
  if (actor === "system") return "a rendszer";
  if (actor === "cli") return "parancssor";
  if (actor === "seed") return "kezdő adatok";
  if (actor.startsWith("migration ")) return "átállás (migráció)";
  return actor;
}

/** One entry of a user's log, as a sentence. An action this page does not
 * know is shown under its wire name, never dropped. */
export function eventLine(event: UserEvent): string {
  const detail = event.detail ?? {};
  switch (event.action) {
    case "user.created":
      return `Létrehozva, aktív (${roleWord(detail.role)})`;
    case "user.recorded":
      return `Átvéve a korábbi rendszerből: ${stateWord(event.to_state)}`;
    case "user.invited":
      return event.from_state === null
        ? `Meghívva (${roleWord(detail.role)})`
        : "Új meghívó link — a lejárt helyett";
    case "user.active":
      return "Beállította a jelszavát — aktív";
    case "user.expired":
      return "A meghívó lejárt, nem használták fel";
    case "user.deactivated":
      return "Letiltva — a belépései megszűntek";
    case "user.reactivated":
      return event.to_state === "invited" ? "Visszakapcsolva, új meghívóval" : "Visszakapcsolva";
    case "user.role":
      return `Szerep: ${roleWord(detail.old)} → ${roleWord(detail.new)}`;
    case "user.name":
      return `Név: ${String(detail.old || "—")} → ${String(detail.new || "—")}`;
    case "user.password":
      if (detail.how === "reset_link") return "Új jelszó a visszaállító linkkel";
      if (detail.how === "cli") return "Új jelszó parancssorból";
      return "Jelszót cserélt";
    case "user.link":
      return detail.purpose === "reset" ? "Jelszó-visszaállító link kiadva" : "Új meghívó link kiadva";
    case "user.mail":
      return mailLine(detail);
    default:
      return event.action;
  }
}
